/**
 * useTasks — the React binding for the tasks feature, scoped to the active room.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link TaskStore}; the
 * store calls the api facade. Pages never import the store or the api directly.
 *
 * Scope: tasks belong to a room, so this hook reads the active room from
 * {@link useActiveRoom} and loads/creates tasks against it. When no room is
 * active it exposes an empty list and `roomId: null` so pages can prompt the
 * user to pick a room instead of erroring.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useActiveRoom } from '../rooms/RoomContext';
import { TaskStore } from './taskStore';
import { buildResearchQuestion } from './kbResearch';
import { sortTasks } from './taskModel';
import type { Task, TaskDraft, TaskStatus } from './types';

export interface UseTasksResult {
  /** The room these tasks are scoped to, or null when none is active. */
  roomId: string | null;
  /** The active room's tasks (priority- then recency-sorted). */
  tasks: Task[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the active room's tasks. */
  refresh: () => Promise<void>;
  /** Creates a task in the active room. Throws if no room is active. */
  create: (draft: TaskDraft) => Promise<Task>;
  /**
   * Grounds an existing task against the team knowledge base and PERSISTS the
   * resulting AI 'Knowledge Base Research' on the task (S3 body only), so it
   * surfaces in the task page's Knowledge Base Research section. Builds the
   * enriched question from the task's title + description via
   * {@link buildResearchQuestion} and reuses the SAME RetrieveAndGenerate path
   * the task page uses. Throws on KB/model failure — callers treat research as
   * best-effort so a failure never blocks task creation.
   */
  researchTask: (task: Task) => Promise<Task>;
  /** Applies a draft's fields to an existing task. */
  update: (task: Task, draft: TaskDraft) => Promise<Task>;
  /** Moves a task to a new status (Kanban column change). */
  move: (task: Task, status: TaskStatus) => Promise<Task>;
  /** Assigns a task to a sprint, or returns it to the backlog with `null`. */
  assignToSprint: (task: Task, sprintId: string | null) => Promise<Task>;
  /** Soft-deletes a task. */
  remove: (id: string) => Promise<void>;
}

export function useTasks(): UseTasksResult {
  const api = useApi();
  const store = useMemo(() => new TaskStore(api), [api]);
  const { activeRoomId } = useActiveRoom();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!activeRoomId) {
      setTasks([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listByRoom(activeRoomId);
      setTasks(sortTasks(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [store, activeRoomId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (draft: TaskDraft) => {
      if (!activeRoomId) {
        throw new Error('Select a room before creating a task.');
      }
      const saved = await store.create(activeRoomId, draft);
      await refresh();
      return saved;
    },
    [store, activeRoomId, refresh],
  );

  const researchTask = useCallback(
    async (task: Task) => {
      // Enrich the task (title + description + any "why" context) into a
      // self-contained KB question, then ground + persist via the SAME
      // RetrieveAndGenerate path the task page uses. The KB is resolved
      // server-side — no resource identifiers are sent.
      const question = buildResearchQuestion(task);
      const saved = await store.researchAndSave(task, { question });
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const update = useCallback(
    async (task: Task, draft: TaskDraft) => {
      const saved = await store.applyDraft(task, draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const move = useCallback(
    async (task: Task, status: TaskStatus) => {
      // Optimistic: reflect the column change immediately, then persist.
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status } : t)),
      );
      try {
        const saved = await store.updateStatus(task, status);
        await refresh();
        return saved;
      } catch (e) {
        await refresh(); // revert to server truth on failure
        throw e;
      }
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  const assignToSprint = useCallback(
    async (task: Task, sprintId: string | null) => {
      // Optimistic: reflect the sprint change immediately, then persist.
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, sprintId: sprintId ?? null } : t,
        ),
      );
      try {
        const saved = await store.assignToSprint(task, sprintId);
        await refresh();
        return saved;
      } catch (e) {
        await refresh(); // revert to server truth on failure
        throw e;
      }
    },
    [store, refresh],
  );

  return {
    roomId: activeRoomId,
    tasks,
    loading,
    error,
    refresh,
    create,
    researchTask,
    update,
    move,
    assignToSprint,
    remove,
  };
}
